import axios from "axios"

import { useUserSessionStore } from "@/store/userSession.js"
import { useAppStateStore } from "@/store/appState.js"

import { eventBus } from "@/plugins/eventBus.js"

import * as environment from "@/services/environment.js"

import axiosRetry from "axios-retry"

let refreshSessionInProgress = false
let globalAbortController = new AbortController()

const endpointsNotRequiringSecret = ["/api/v1/sessions", "/api/v1/accounts", "/api/v2/magic_sessions"]

const hasSessionExpired = (error) => {
  if (!error.response || !error.config) {
    return false
  }

  return error.response.status === 403 && error.config.headers["X-Secret"]
}

const refreshSession = async () => {
  try {
    refreshSessionInProgress = true
    const userSessionStore = useUserSessionStore()
    await userSessionStore.refreshSession()
  } catch {
    eventBus.$emit("refresh-session-error")
  } finally {
    refreshSessionInProgress = false
  }
}

const setup = function () {
  const revision = process.env.NODE_ENV === "development" ? "dev" : environment.getRevision()
  axios.defaults.timeout = 30000
  axios.interceptors.request.use(
    (config) => {
      if (!config.signal) {
        config.signal = globalAbortController.signal
      }

      const userSessionStore = useUserSessionStore()
      const session = userSessionStore.session

      if (session !== null && config.withCredentials !== false) {
        config.headers["X-Secret-Id"] = session.secret_id
        config.headers["X-Secret"] = session.secret
      }
      if (config.url.startsWith("/api/")) {
        config.headers["X-Pwa-Revision-Number"] = revision
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  axiosRetry(axios, {
    retries: 2,
    retryDelay: (retryCount) => retryCount * 1000,
    retryCondition: (error) => {
      const appStateStore = useAppStateStore()
      if (endpointsNotRequiringSecret.includes(error.config?.url) || appStateStore.webViewMode) {
        return false
      }

      const sessionExpired = hasSessionExpired(error)
      if (!sessionExpired) {
        return false
      }

      if (!refreshSessionInProgress) {
        refreshSession()
      }

      return true
    }
  })

  axios.interceptors.response.use(
    (response) => {
      if (response.data) {
        return response.data
      }
      return response
    },
    (error) => {
      // This happens after axiosRetry retries 2 times or its retryCondition returns false
      const isUnauthorized = error.response?.status === 401
      const sessionExpired = hasSessionExpired(error)

      if (isUnauthorized || sessionExpired) {
        eventBus.$emit("unauthorized-access")
        // There could be an ongoing request at the time of destroying the session, need to cancel them
        globalAbortController.abort("Request cancelled: session invalid.")
        setTimeout(() => {
          globalAbortController = new AbortController()
        })
      }

      throw error
    }
  )
}

const getDataFromApiError = function (error) {
  let errorCode = -1
  let errorData = {}
  //Axios timeout
  if (error) {
    if (error.code === "ECONNABORTED") {
      errorCode = 408
    } else if (error.response) {
      errorCode = error.response.status
      if (error.response.data?.errors) {
        errorData = error.response.data.errors
      }
    }
  }

  const ret = {
    errorCode,
    errorData
  }

  //Create error message from error data
  //Can be used for non-obious API errors
  if (Object.keys(errorData).length) {
    ret.errorMessage = JSON.stringify(errorData).replace(/{|}|"/g, "")
  }

  return ret
}

export default {
  setup,
  getDataFromApiError
}
